import { useState } from 'react';
import { Banner } from '@els/els-react--banner';
import { ChartActions, ChartPermission, ErrorCard, ErrorFormField, SectionDivider } from 'components/common';
import { ELSFlex, ELSFlexItem } from 'components/common/els';
import { ChartViewProps } from 'components/features/shared/constants';
import { ChartMetaFormField } from 'models/ui';
import { DECISION_MAKER_HINT, DNR_NOTICE, FormField, FormFieldLabel, SectionTitle } from './constants';
import './do.not.resuscitate.scss';

export interface DoNotResuscitateViewProps extends ChartViewProps {
  showDecisionMakerFields: boolean;
  showPatientConsent: boolean;
  showChartActions?: boolean;
  isInstructorEditMode?: boolean;
}

const hasFieldValue = (formField: ChartMetaFormField | undefined): boolean => {
  if (!formField) return false;
  const value = Array.isArray(formField.value) ? formField.value : String(formField.value ?? '').trim();
  return Array.isArray(value) ? value.length > 0 : value.length > 0;
};

const getInlineField = (formField: ChartMetaFormField | undefined): ChartMetaFormField | undefined => {
  if (!formField) return formField;
  return { ...formField, label: '' };
};

const getConditionalRequiredField = (formField: ChartMetaFormField | undefined, isRequired: boolean): ChartMetaFormField | undefined => {
  if (!formField) return formField;
  return { ...formField, isRequired: isRequired && !hasFieldValue(formField) };
};

const renderDropdown = (formField: ChartMetaFormField | undefined, formSubmittedCount: number, modifier: 'select') => (
  <div className={!hasFieldValue(formField) ? `dnr__dropdown-placeholder dnr__dropdown-placeholder--${modifier}` : undefined}>
    <ErrorFormField formField={formField} formSubmittedCount={formSubmittedCount} />
  </div>
);


const DoNotResuscitateView = (props: DoNotResuscitateViewProps) => {
  const { formFieldMap, formSubmittedCount, showDecisionMakerFields, showPatientConsent, showChartActions = true, isInstructorEditMode = false } = props;
  const [isNoticeVisible, setIsNoticeVisible] = useState(true);

  return (
    <div className="dnr">
      <h1>{SectionTitle.DO_NOT_RESUSCITATE}</h1>
      <ErrorCard chartMetaFormFields={formFieldMap} showErrorCard={formSubmittedCount > 0} />

      <ELSFlex className="dnr__charting-row u-flex-gutter-2x u-els-margin-top-2x u-els-margin-bottom-1x1o2" wrap left>
        <ELSFlexItem className="dnr__charting-item" w="1o3" md="1o1">
          <ErrorFormField formField={formFieldMap.get(FormField.STATE)} formSubmittedCount={formSubmittedCount} />
        </ELSFlexItem>
      </ELSFlex>

      <Banner className="dnr__notice" isVisible={isNoticeVisible} onClose={() => setIsNoticeVisible(false)}>
        {DNR_NOTICE}
      </Banner>

      <SectionDivider mt />

      <h2 className="dnr__section-title">{SectionTitle.PATIENT_CONSENT}</h2>

      <ELSFlex className="u-els-margin-bottom-1x1o2" wrap left>
        <ELSFlexItem w="1o2" md="1o1">
          <ErrorFormField formField={formFieldMap.get(FormField.PATIENT_FULL_NAME)} formSubmittedCount={formSubmittedCount} />
        </ELSFlexItem>
      </ELSFlex>

      <div className="dnr__capacity-row">
        <span className="dnr__capacity-label">{FormFieldLabel.DECISION_CAPACITY}</span>
        <ErrorFormField
          className="dnr__capacity"
          formField={getInlineField(formFieldMap.get(FormField.DECISION_CAPACITY))}
          formSubmittedCount={formSubmittedCount}
          options={{ displayHorizontal: true }}
          useDefaultMargin={false}
          hideAsterisk
        />
      </div>

      {showDecisionMakerFields && (
        <ELSFlex className="dnr__decision-maker-row u-flex-gutter-2x u-els-margin-top-1x u-els-margin-bottom-1x1o2" wrap left>
          <ELSFlexItem w="1o3" md="1o1">
            {renderDropdown(getConditionalRequiredField(formFieldMap.get(FormField.DECISION_MAKER), showDecisionMakerFields), formSubmittedCount, 'select')}
            <p className="dnr__decision-maker-hint">{DECISION_MAKER_HINT}</p>
          </ELSFlexItem>
          <ELSFlexItem w="1o3" md="1o1">
            <ErrorFormField formField={getConditionalRequiredField(formFieldMap.get(FormField.DECISION_MAKER_NAME), showDecisionMakerFields)} formSubmittedCount={formSubmittedCount} />
          </ELSFlexItem>
          <ELSFlexItem w="1o3" md="1o1">
            <ErrorFormField formField={getConditionalRequiredField(formFieldMap.get(FormField.RELATIONSHIP_TO_PATIENT), showDecisionMakerFields)} formSubmittedCount={formSubmittedCount} />
          </ELSFlexItem>
        </ELSFlex>
      )}

      {showPatientConsent && (
        <div className="dnr__checkbox u-els-margin-top-1x u-els-margin-bottom-1x">
          {!hasFieldValue(formFieldMap.get(FormField.PATIENT_CONSENT_CHECKED)) && <span className="chart-input__label-asterisk">*</span>}
          <ErrorFormField formField={formFieldMap.get(FormField.PATIENT_CONSENT_CHECKED)} formSubmittedCount={formSubmittedCount} />
        </div>
      )}

      {showPatientConsent && (
        <ELSFlex className="u-flex-gutter-2x u-els-margin-bottom-1x1o2" wrap left>
          <ELSFlexItem w="1o3" md="1o1">
            <ErrorFormField formField={getConditionalRequiredField(formFieldMap.get(FormField.PATIENT_CONSENT_SIGNATURE), true)} formSubmittedCount={formSubmittedCount} />
          </ELSFlexItem>
        </ELSFlex>
      )}

      <SectionDivider mt />

      <h2 className="dnr__section-title">{SectionTitle.PROVIDER_ORDER}</h2>

      <div className="dnr__checkbox u-els-margin-top-1x u-els-margin-bottom-1x">
        {!hasFieldValue(formFieldMap.get(FormField.PROVIDER_ORDER_CHECKED)) && <span className="chart-input__label-asterisk">*</span>}
        <ErrorFormField formField={formFieldMap.get(FormField.PROVIDER_ORDER_CHECKED)} formSubmittedCount={formSubmittedCount} />
      </div>

      <ELSFlex className="u-flex-gutter-2x u-els-margin-bottom-1x1o2" wrap left>
        <ELSFlexItem w="1o3" md="1o1">
          <ErrorFormField formField={formFieldMap.get(FormField.PROVIDER_NAME)} formSubmittedCount={formSubmittedCount} />
        </ELSFlexItem>
        <ELSFlexItem w="1o3" md="1o1">
          {renderDropdown(formFieldMap.get(FormField.PROVIDER_TYPE), formSubmittedCount, 'select')}
        </ELSFlexItem>
        <ELSFlexItem w="1o3" md="1o1">
          <ErrorFormField formField={formFieldMap.get(FormField.EMERGENCY_PHONE)} formSubmittedCount={formSubmittedCount} />
        </ELSFlexItem>
      </ELSFlex>

      <ELSFlex className="u-flex-gutter-2x u-els-margin-bottom-1x1o2" wrap left>
        <ELSFlexItem w="1o3" md="1o1">
          <ErrorFormField formField={formFieldMap.get(FormField.PROVIDER_SIGNATURE)} formSubmittedCount={formSubmittedCount} />
        </ELSFlexItem>
      </ELSFlex>

      {showChartActions && (
        <ChartPermission>{({ allowSaveFragment }) => (allowSaveFragment || isInstructorEditMode) && <ChartActions {...props.chartActionsProps} />}</ChartPermission>
      )}
    </div>
  );
};

export default DoNotResuscitateView;
